/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Location} from '@angular/common';
import {ChangeDetectionStrategy, Component, OnInit, Optional, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {NbTabComponent, NbTabsetComponent, NbWindowRef,} from '@nebular/theme';
import {BreadcrumbDto, DamCatalogPlain, DamItem, DamItemVersion, DamRelatedCollection, FileService, Permission,} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {translations} from '../../../../../environments/translations.ru';
import {CreateUpdateComponent} from '../../../../admin-shared/crud-components/create-update/create-update.component';
import {CrudConfig} from '../../../../admin-shared/interfaces/crud.config';
import {FormConfigService} from '../../../../admin-shared/services/form-config.service';
import {deepCopy} from '../../../ecommerce/orders/helpers';
import {DamService} from '../../dam.service';
import {DamItemVersionTypeCountWSelected} from '../dam-table/dam-catalog.component';
import {FILES_FORM} from '../files-forms.config';

@Component({
  selector: 'create-update-files',
  templateUrl: './create-update-files.component.html',
  styleUrls: ['./create-update-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateUpdateFilesComponent extends CreateUpdateComponent<DamItem> implements OnInit {
  @ViewChild('tabset') tabsetEl: NbTabsetComponent;
  @ViewChild('attachedElementsTab') attachedElementsTab: NbTabComponent;
  @ViewChild('infoTab') infoTabEl: NbTabComponent;
  @ViewChild('permissionsTab') permissionsTabEl: NbTabComponent;
  @ViewChild('featureTab') featureTabEl: NbTabComponent;
  catalogBreadCrumbs: Observable<BreadcrumbDto[]>;
  featureTabSpinner = false;
  title = translations.files;
  uri = '/dam';
  absolutePath = '/cabinet/dam';
  model: any;
  isFormUpload = false;
  redirectToEditPage = true;
  createdDate: Date;
  modelFeaturesChanges = false;
  protected entity: any;
  config: CrudConfig = {
    uri: this.uri,
    columns: FILES_FORM,
    title: this.title,
    absolutePath: this.absolutePath,
  };
  activeTab = 'info';
  loadFeatures = false;
  public tabsIDs = {
    info: 'infoTab',
    permissions: 'permissionsTab',
    features: 'features',
    attachedElements: 'attachedElements',
    changeHistory: 'changeHistory',
  };
  currentDamVersion: DamItemVersion;

  damRelatedCollection: DamRelatedCollection[];
  permissions: Permission = null;
  write = false;
  create = false;
  delete = false;
  administration = false;
  damItemVersions: DamItemVersion[] = [];
  fileExtensions: DamItemVersionTypeCountWSelected[] = [];

  constructor(
    protected readonly formConfigService: FormConfigService,
    protected readonly route: ActivatedRoute,
    protected readonly location: Location,
    @Optional() protected readonly windowRef: NbWindowRef,
    protected readonly fileService: FileService,
    protected readonly damService: DamService
  ) {
    super(formConfigService, route, location, windowRef, fileService);
  }

  ngOnInit(): void {
    super.setConfig(this.config);
    super.ngOnInit();
  }

  afterSetModel(): void {
    if (this.model.id) {
      this.loadDamItemVersions();
      this.loadDamItemPermissions();
    }

    this.editName = this.model.header;
    this.catalogBreadCrumbs = this.damService.getCatalogBreadCrumbs(this.model.damCatalogId);
    this.model.startDate = this.model.startDate === null ? null : new Date(this.model.startDate);
    this.model.endDate = this.model.endDate === null ? null : new Date(this.model.endDate);
    this.createdDate = deepCopy(this.model.createdAt);
    // this.model.endDate = "";
    this.cdr.detectChanges();

  }

  beforeSubmit(): void {
    // this.model.fileInput = this.form.controls.fileInput.value;
    this.entity = {};
    this.entity.termIDs = 'terms' in this.model && Array.isArray(this.model.terms) ? this.model.terms.map(term => term.id) : [];
    // this.entity.terms = [];
    this.entity.id = this.model.id;
    this.entity.createdAt = this.model.createdAt;
    this.entity.updatedAt = this.model.updatedAt;
    this.entity.header = this.model.header;
    this.entity.damCatalogId = this.model.damCatalogId;
    this.entity.startDate = this.model.startDate === null ? null : this.convertDate(this.model.startDate);
    this.entity.endDate = this.model.endDate === null ? null : this.convertDate(this.model.endDate);
    this.entity.currentDamItemVersionId = this.model.currentDamItemVersionId;
    this.entity.deleted = this.model.deleted;
    this.entity.enabled = this.model.enabled;
    this.entity.approved = this.model.approved;
    this.entity.syncUid = this.model.syncUid;
    this.entity.damCatalogs = 'damCatalogs' in this.model && Array.isArray(this.model.damCatalogs) ? this.model.damCatalogs.map(element => element.id) : [];

  }

  onSubmit(): void {
    this.loading = true;
    this.beforeSubmit();
    let submitResult;

    if (this.isFormUpload === true) {
      submitResult = this.compoApiService.postForm<any, string>(this.uri + '/update/' + (this.id !== null ? String(this.id) : ''), this.entity);
    } else {
      submitResult = this.compoApiService.post<any, string>(this.uri + '/update/' + (this.id !== null ? String(this.id) : ''), this.entity);
    }

    submitResult.pipe(takeUntil(this.unsubscribe))
      .subscribe(success => this.handleSuccess(success), error => this.handleError(error));
  }

  afterSubmit(): void {
    if (this.redirectToEditPage && this.createdId !== undefined) {
      const url = this.config.absolutePath + '/item/edit/' + this.createdId;

      this.router.navigateByUrl(url);
      if (this.editMode) {
        window.location.reload();
      }
    }
  }

  public download(fileName: string): void {
    this.fileService.downloadUrl('/dam/download/' + fileName)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        FileService.saveAs(response, fileName);
      });
  }

  removeAdditionalCatalog(catalog: DamCatalogPlain) {
    const index = this.model.damCatalogs.findIndex(el => el.id === catalog.id);
    this.model.damCatalogs.splice(index, 1);
    this.onSubmit();
  }

  insertAdditionalCatalog(catalog: DamCatalogPlain) {
    if (!this.model.damCatalogs.some(catalogs => catalogs.id === catalog.id)) {
      this.model.damCatalogs.push(catalog);
      this.onSubmit();
    } else {
      this.warningToast(translations.relationExists);
    }
  }

  public onFeatureModelChanges(isModelChanged: boolean): void {
    this.modelFeaturesChanges = isModelChanged;
  }

  back(): void {
    this.location.back();
  }

  public getTab($event): void {
    if ($event.tabId === this.tabsIDs.features && !this.loadFeatures) {
      this.loadFeatures = true;
    }

    if ($event.tabId !== this.tabsIDs.features && this.activeTab === this.tabsIDs.features && this.modelFeaturesChanges === true) {
      this.activeTab = this.tabsIDs.features;
      this.errorToast(translations.unsavedChanges);
      this.tabsetEl.selectTab(this.featureTabEl);
      return;
    }
    this.activeTab = $event.tabId;

  }

  private convertDate(date: Date): string {
    const ye = new Intl.DateTimeFormat('ru', {year: 'numeric'}).format(date);
    const mo = new Intl.DateTimeFormat('ru', {month: '2-digit'}).format(date);
    const da = new Intl.DateTimeFormat('ru', {day: '2-digit'}).format(date);

    return ye + '-' + mo + '-' + da;
  }

  onVersionSelect(version: DamItemVersion) {
    this.currentDamVersion = version;
  }

  loadDamItemVersions() {
    this.compoApiService
      .get<DamItemVersion[]>('/dam/' + this.model.id + '/versions')
      .pipe(take(1))
      .subscribe(response => {
        this.damItemVersions = response.data;
        if (this.damItemVersions[0]) {
          this.currentDamVersion = this.damItemVersions[0];
        }
        const extensionsMap = new Map<string, DamItemVersionTypeCountWSelected>();
        this.damItemVersions.forEach(version => {
          const mapItem = extensionsMap.get(version?.mimeType) || undefined;
          if (mapItem) {
            extensionsMap.set(version.mimeType, {typeOfFile: version.mimeType, count: mapItem.count + 1, selected: false});
          } else {
            extensionsMap.set(version.mimeType, {typeOfFile: version.mimeType, count: 1, selected: false});
          }
        });
        extensionsMap.forEach(mapItem => {
          this.fileExtensions.push(mapItem);
        });
      });
  }

  loadDamItemPermissions() {
    this.compoApiService
      .get<Permission>('/dam/permissions/' + this.model.id)
      .pipe(take(1))
      .subscribe(response => {
        this.permissions = response.data;
        if (this.permissions.myPermissions.indexOf('2') !== -1) {
          this.write = true;
        }
        if (this.permissions.myPermissions.indexOf('4') !== -1) {
          this.create = true;
        }
        if (this.permissions.myPermissions.indexOf('8') !== -1) {
          this.delete = true;
        }
        if (this.permissions.myPermissions.indexOf('16') !== -1) {
          this.administration = true;
        }
        this.cdr.detectChanges();
      });
  }

  protected setModel(model: DamItem): void {
    super.setModel(model);
    if (this.editMode) {
      this.compoApiService
        .get<DamRelatedCollection[]>(this.uri + '/collections/' + this.model.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(response => {
          this.damRelatedCollection = response.data;
          this.cdr.detectChanges();
        });
    }
  }
}
